import React, { useState, useEffect } from "react";
import { C, SHEIKHS, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  getMosques, createMosque,
  getMosquePrograms, createMosqueProgram,
  getMosqueTalks, createMosqueTalk,
  getMosqueScienceDays, createMosqueScienceDay,
} from "./supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  Landmark, MapPin, Users, CheckCircle, AlertCircle, Plus, Search,
  Calendar, Target, Phone, FileText, BookOpen, GraduationCap,
  Mic, Activity, Megaphone, Award, BarChart3, PieChart as PieChartIcon
} from "lucide-react";

/* ✦ محوّلات صفوف DB (snake_case) → camelCase الذي تقرأه الواجهة. */
function mapMosqueRow(r) {
  return {
    id: r.id,
    name: r.name,
    district: r.district || "",
    location: r.location || "",
    cooperationStatus: r.cooperation_status || "active",
    imam: r.imam || {},
    muezzin: r.muezzin || {},
    stats: r.stats || {},
    notes: r.notes || null,
  };
}

function mapProgramRow(r) {
  return {
    id: r.id,
    name: r.name,
    type: r.type || "",
    mosque: r.mosque || "",
    executor: r.executor || "",
    date: r.program_date || "",
    attendance: Number(r.attendance) || 0,
    report: r.report || "",
    attachments: r.attachments || "",
  };
}

function mapTalkRow(r) {
  return {
    id: r.id,
    title: r.title,
    speaker: r.speaker || "",
    mosque: r.mosque || "",
    date: r.talk_date || "",
    attendance: Number(r.attendance) || 0,
    material: r.material || "",
    attachments: r.attachments || "",
    report: r.report || "",
  };
}

function mapScienceDayRow(r) {
  return {
    id: r.id,
    name: r.name,
    date: r.day_date || "",
    lessonsCount: Number(r.lessons_count) || 0,
    attendance: Number(r.attendance) || 0,
    finalReport: r.final_report || "",
    photos: r.photos || "",
  };
}

/* ═════════════════════════════════════════════
   ✦ Dawah Page — الدعوة (المفتاح mosques مُبقىً للراوتر والصلاحيات)
   ═════════════════════════════════════════════ */
export default function MosquesPage({ user }) {
  const [tab, setTab] = useState("overview");
  const [triggers, setTriggers] = useState({ mosque: 0 });
  const perms = getPerms(user);

  const [mosques, setMosques] = useState([]);
  const [loadingMosques, setLoadingMosques] = useState(true);
  useEffect(() => { getMosques().then(d => { setMosques((d || []).map(mapMosqueRow)); setLoadingMosques(false); }).catch(() => setLoadingMosques(false)); }, []);

  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  useEffect(() => { getMosquePrograms().then(d => { setPrograms((d || []).map(mapProgramRow)); setLoadingPrograms(false); }).catch(() => setLoadingPrograms(false)); }, []);

  const [talks, setTalks] = useState([]);
  const [loadingTalks, setLoadingTalks] = useState(true);
  useEffect(() => { getMosqueTalks().then(d => { setTalks((d || []).map(mapTalkRow)); setLoadingTalks(false); }).catch(() => setLoadingTalks(false)); }, []);

  const [scienceDays, setScienceDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(true);
  useEffect(() => { getMosqueScienceDays().then(d => { setScienceDays((d || []).map(mapScienceDayRow)); setLoadingDays(false); }).catch(() => setLoadingDays(false)); }, []);

  const tabs = [
    { key: "overview", label: "نظرة عامة", icon: Activity },
    { key: "mosques", label: "الجوامع والمساجد", icon: Landmark },
    { key: "programs", label: "البرامج الدعوية", icon: Award },
    { key: "talks", label: "الكلمات والمحاضرات", icon: Megaphone },
    { key: "scienceDays", label: "الأيام العلمية", icon: GraduationCap },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  const reportSources = [
    {
      label: "البرامج الدعوية", mode: "monthly", dateField: "date", rows: programs,
      columns: [
        { key: "name", label: "البرنامج" },
        { key: "type", label: "النوع" },
        { key: "mosque", label: "المسجد" },
        { key: "executor", label: "المنفّذ" },
        { key: "date", label: "التاريخ" },
        { key: "attendance", label: "الحضور" },
      ],
    },
    {
      label: "الكلمات والمحاضرات", mode: "monthly", dateField: "date", rows: talks,
      columns: [
        { key: "title", label: "العنوان" },
        { key: "speaker", label: "الملقي" },
        { key: "mosque", label: "المسجد" },
        { key: "date", label: "التاريخ" },
        { key: "attendance", label: "الحضور" },
      ],
    },
    {
      label: "الأيام العلمية", mode: "monthly", dateField: "date", rows: scienceDays,
      columns: [
        { key: "name", label: "اليوم العلمي" },
        { key: "date", label: "التاريخ" },
        { key: "lessonsCount", label: "عدد الدروس" },
        { key: "attendance", label: "الحضور" },
      ],
    },
    { label: "الجوامع والمساجد", mode: "cumulative", rows: mosques },
  ];

  return (
    <div>
      <ModuleHeader icon={Landmark} title="الدعوة" subtitle="متابعة الجوامع والمساجد والأنشطة الدعوية">
        <MonthlyReportButton moduleTitle="الدعوة" sources={reportSources} budget={620000} />
      </ModuleHeader>

      {perms.canEdit && (
        <div style={mq.row}>
          <button style={mq.btn} onClick={() => fireQuick("mosques", "mosque")}>
            <Landmark size={16} color="#0891b2" />
            <span>إضافة موقع تنفيذ</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview" && <OverviewTab mosques={mosques} />}
      {tab === "mosques" && <MosquesTab canEdit={perms.canEdit} triggerOpen={triggers.mosque} mosques={mosques} setMosques={setMosques} loading={loadingMosques} />}
      {tab === "programs" && <ProgramsTab canEdit={perms.canEdit} programs={programs} setPrograms={setPrograms} loading={loadingPrograms} mosques={mosques} />}
      {tab === "talks" && <TalksTab canEdit={perms.canEdit} talks={talks} setTalks={setTalks} loading={loadingTalks} mosques={mosques} />}
      {tab === "scienceDays" && <ScienceDaysTab canEdit={perms.canEdit} days={scienceDays} setDays={setScienceDays} loading={loadingDays} />}
      {tab === "meetings" && <MeetingsTab departmentName="الدعوة" module="mosques" />}
      {tab === "transactions" && <TransactionsTab budget={620000} module="mosques" />}
      {tab === "plan" && <OperationalPlanTab departmentKey="mosques" />}
      {tab === "reports" && <ReportsTab departmentName="الدعوة" />}
    </div>
  );
}

const mq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: C.white, color: C.primary, border: `1px solid ${C.border}`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

/* ─────────────────────────────────────────────
   ✦ Cooperation Status + Initial Form
   ───────────────────────────────────────────── */
const COOP_STATUSES = [
  { key: "active", label: "نشط", color: C.success },
  { key: "paused", label: "متوقف", color: C.warning },
  { key: "needs_followup", label: "يحتاج متابعة", color: C.danger },
];

const CONTACT_PREFS = ["هاتف", "بريد", "كلاهما"];

const INITIAL_MOSQUE = {
  name: "", district: "", location: "",
  cooperationStatus: "active",
  imam:    { name: "", phone: "", email: "", preferredContact: "هاتف", notes: "" },
  muezzin: { name: "", phone: "", email: "", preferredContact: "هاتف", notes: "" },
  stats:   { talks: "", lectures: "", lessons: "", scienceDays: "", programs: "", beneficiaries: "", lastActivity: "" },
  notes: "",
};

/* ─────────────────────────────────────────────
   ✦ Overview Tab — نظرة عامة دعوية
   ───────────────────────────────────────────── */
function OverviewTab({ mosques }) {
  const data = mosques;

  const sum = (key) => data.reduce((s, m) => s + (Number(m.stats?.[key]) || 0), 0);
  const talks         = sum("talks");
  const lectures      = sum("lectures");
  const lessons       = sum("lessons");
  const scienceDays   = sum("scienceDays");
  const programs      = sum("programs");
  const beneficiaries = sum("beneficiaries");

  const coopData = COOP_STATUSES.map(s => ({
    name: s.label,
    value: data.filter(m => m.cooperationStatus === s.key).length,
    color: s.color,
  }));

  const activityData = [
    { name: "الكلمات",       value: talks,       color: "#0891b2" },
    { name: "المحاضرات",     value: lectures,    color: "#7c3aed" },
    { name: "الدروس",        value: lessons,     color: C.teal },
    { name: "الأيام العلمية", value: scienceDays, color: C.accent },
    { name: "البرامج",       value: programs,    color: "#ea580c" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="الجوامع والمساجد" value={data.length} icon={Landmark} color="#0891b2" />
        <KPICard title="الكلمات الدعوية" value={talks.toLocaleString()} icon={Mic} color={C.teal} />
        <KPICard title="المحاضرات" value={lectures.toLocaleString()} icon={Megaphone} color="#7c3aed" />
        <KPICard title="الدروس العلمية" value={lessons.toLocaleString()} icon={BookOpen} color={C.primary} />
        <KPICard title="الأيام العلمية" value={scienceDays.toLocaleString()} icon={GraduationCap} color={C.accent} />
        <KPICard title="البرامج الدعوية" value={programs.toLocaleString()} icon={Award} color="#ea580c" />
        <KPICard title="المستفيدون" value={beneficiaries.toLocaleString()} icon={Users} color={C.success} />
      </div>

      <div style={styles.chartsRow}>
        {/* Donut — توزيع المواقع حسب حالة التعاون */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع المواقع حسب حالة التعاون</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={coopData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                {coopData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} موقع`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {coopData.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Bar — مؤشرات الأنشطة الدعوية */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>مؤشرات الأنشطة الدعوية</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={activityData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [v.toLocaleString()]}
              />
              <Bar dataKey="value" name="العدد" radius={[6, 6, 0, 0]}>
                {activityData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

function MosquesTab({ canEdit, triggerOpen = 0, mosques, setMosques, loading }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [form, setForm] = useState(INITIAL_MOSQUE);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  function setImam(field, v)    { setForm(prev => ({ ...prev, imam:    { ...prev.imam,    [field]: v } })); }
  function setMuezzin(field, v) { setForm(prev => ({ ...prev, muezzin: { ...prev.muezzin, [field]: v } })); }
  function setStat(field, v)    { setForm(prev => ({ ...prev, stats:   { ...prev.stats,   [field]: v } })); }

  async function handleSave() {
    if (!form.name || !form.district) {
      showToast("الاسم والحي مطلوبان", "error");
      return;
    }
    const toNum = (v) => Number(v) || 0;
    const payload = {
      name: form.name,
      district: form.district,
      location: form.location || "",
      cooperation_status: form.cooperationStatus || "active",
      imam:    { ...form.imam },
      muezzin: { ...form.muezzin },
      stats: {
        talks:         toNum(form.stats.talks),
        lectures:      toNum(form.stats.lectures),
        lessons:       toNum(form.stats.lessons),
        scienceDays:   toNum(form.stats.scienceDays),
        programs:      toNum(form.stats.programs),
        beneficiaries: toNum(form.stats.beneficiaries),
        lastActivity:  form.stats.lastActivity || "",
      },
      notes: form.notes || null,
    };
    try {
      const created = await createMosque(payload);
      setMosques(prev => [mapMosqueRow(created), ...prev]);
      setForm(INITIAL_MOSQUE);
      setShowAdd(false);
      showToast("تمت إضافة الموقع بنجاح");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ الموقع", "error");
    }
  }

  const filtered = mosques.filter(m => {
    const matchSearch = m.name.includes(search) || m.district.includes(search) || (m.imam?.name || "").includes(search);
    const matchStatus = filterStatus === "all" || m.cooperationStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="الجوامع والمساجد" value={mosques.length} icon={Landmark} color="#0891b2" />
        <KPICard title="نشط" value={mosques.filter(m => m.cooperationStatus === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="متوقف" value={mosques.filter(m => m.cooperationStatus === "paused").length} icon={AlertCircle} color={C.warning} />
        <KPICard title="يحتاج متابعة" value={mosques.filter(m => m.cooperationStatus === "needs_followup").length} icon={Phone} color={C.danger} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الحي أو الإمام..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {COOP_STATUSES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <div style={styles.viewToggle}>
              <button onClick={() => setViewMode("grid")} style={{ ...styles.viewBtn, background: viewMode === "grid" ? C.teal : "transparent", color: viewMode === "grid" ? C.white : C.textLight }}>▦</button>
              <button onClick={() => setViewMode("table")} style={{ ...styles.viewBtn, background: viewMode === "table" ? C.teal : "transparent", color: viewMode === "table" ? C.white : C.textLight }}>☰</button>
            </div>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة موقع</Btn>}
          </div>
        </div>
      </Card>

      {viewMode === "grid" ? (
        <div style={styles.mosquesGrid}>
          {filtered.map(m => {
            const coop = COOP_STATUSES.find(c => c.key === m.cooperationStatus) || COOP_STATUSES[0];
            return (
              <Card key={m.id} style={styles.mosqueCard} onClick={() => setSelected(m)}>
                <div style={styles.mosqueHeader}>
                  <div style={{ ...styles.mosqueIcon, background: `${coop.color}15` }}>
                    <Landmark size={24} color={coop.color} />
                  </div>
                  <StatusBadge status={m.cooperationStatus} statuses={COOP_STATUSES} />
                </div>
                <h4 style={styles.mosqueName}>{m.name}</h4>
                <p style={styles.mosqueDistrict}><MapPin size={12} /> {m.district}</p>
                <div style={styles.mosqueStats}>
                  <div style={styles.mosqueStat}>
                    <Mic size={14} color={C.textLight} />
                    <span>{m.imam?.name || "—"}</span>
                  </div>
                  <div style={styles.mosqueStat}>
                    <Activity size={14} color={C.textLight} />
                    <span>آخر نشاط: {m.stats?.lastActivity || "—"}</span>
                  </div>
                </div>
                <div style={styles.mosqueFeatures}>
                  <span style={styles.featureTag}>كلمات: {m.stats?.talks ?? 0}</span>
                  <span style={styles.featureTag}>دروس: {m.stats?.lessons ?? 0}</span>
                  <span style={styles.featureTag}>مستفيدون: {(m.stats?.beneficiaries ?? 0).toLocaleString()}</span>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <DataTable
            columns={[
              { key: "name",     label: "اسم الموقع", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
              { key: "district", label: "الحي", width: "120px" },
              { key: "cooperationStatus", label: "حالة التعاون", width: "130px", render: v => <StatusBadge status={v} statuses={COOP_STATUSES} /> },
              { key: "imam",     label: "الإمام", width: "160px", render: v => v?.name || "—" },
              { key: "stats",    label: "آخر نشاط", width: "120px", render: v => v?.lastActivity || "—" },
            ]}
            data={filtered}
            onRowClick={row => setSelected(row)}
          />
        </Card>
      )}

      {/* Detail Modal */}
      {selected && (
        <Modal title={`موقع — ${selected.name}`} onClose={() => setSelected(null)} width={640}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>الحي:</strong> {selected.district}</div>
            <div style={styles.detailRow}><strong>الموقع:</strong> {selected.location || "—"}</div>
            <div style={styles.detailRow}><strong>حالة التعاون:</strong> <StatusBadge status={selected.cooperationStatus} statuses={COOP_STATUSES} /></div>

            <div style={styles.sectionDivider}>الإمام</div>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.imam?.name || "—"}</div>
            <div style={styles.detailRow}><strong>الجوال:</strong> <span dir="ltr">{selected.imam?.phone || "—"}</span></div>
            <div style={styles.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.imam?.email || "—"}</span></div>
            <div style={styles.detailRow}><strong>التواصل المفضل:</strong> {selected.imam?.preferredContact || "—"}</div>
            {selected.imam?.notes && <div style={styles.detailRow}><strong>ملاحظات:</strong> {selected.imam.notes}</div>}

            <div style={styles.sectionDivider}>المؤذن</div>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.muezzin?.name || "—"}</div>
            <div style={styles.detailRow}><strong>الجوال:</strong> <span dir="ltr">{selected.muezzin?.phone || "—"}</span></div>
            <div style={styles.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.muezzin?.email || "—"}</span></div>
            <div style={styles.detailRow}><strong>التواصل المفضل:</strong> {selected.muezzin?.preferredContact || "—"}</div>
            {selected.muezzin?.notes && <div style={styles.detailRow}><strong>ملاحظات:</strong> {selected.muezzin.notes}</div>}

            <div style={styles.sectionDivider}>الإحصائيات الدعوية</div>
            <div style={styles.detailRow}><strong>الكلمات:</strong> {selected.stats?.talks ?? 0}</div>
            <div style={styles.detailRow}><strong>المحاضرات:</strong> {selected.stats?.lectures ?? 0}</div>
            <div style={styles.detailRow}><strong>الدروس:</strong> {selected.stats?.lessons ?? 0}</div>
            <div style={styles.detailRow}><strong>الأيام العلمية:</strong> {selected.stats?.scienceDays ?? 0}</div>
            <div style={styles.detailRow}><strong>البرامج الدعوية:</strong> {selected.stats?.programs ?? 0}</div>
            <div style={styles.detailRow}><strong>المستفيدون:</strong> {(selected.stats?.beneficiaries ?? 0).toLocaleString()}</div>
            <div style={styles.detailRow}><strong>آخر نشاط:</strong> {selected.stats?.lastActivity || "—"}</div>

            {selected.notes && <div style={styles.detailRow}><strong>ملاحظات عامة:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="إضافة موقع تنفيذ" onClose={() => { setShowAdd(false); setForm(INITIAL_MOSQUE); }} width={820}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="اسم الموقع" required placeholder="مثال: مسجد الإيمان / جامع التوحيد" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الحي" required placeholder="اسم الحي" value={form.district} onChange={v => setForm({ ...form, district: v })} />
              <Field label="الموقع (رابط/إحداثيات)" placeholder="https://maps... أو 26.32,43.97" value={form.location} onChange={v => setForm({ ...form, location: v })} />
            </div>
            <Field label="حالة التعاون" type="select" options={COOP_STATUSES.map(s => s.label)}
              value={COOP_STATUSES.find(s => s.key === form.cooperationStatus)?.label || "نشط"}
              onChange={v => setForm({ ...form, cooperationStatus: (COOP_STATUSES.find(s => s.label === v)?.key) || "active" })} />

            {/* Imam */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 6 }}>بيانات الإمام</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="الاسم" placeholder="اسم الإمام" value={form.imam.name} onChange={v => setImam("name", v)} />
                <Field label="الجوال" placeholder="05xxxxxxxx" value={form.imam.phone} onChange={v => setImam("phone", v)} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="البريد الإلكتروني" placeholder="email@example.com" value={form.imam.email} onChange={v => setImam("email", v)} />
                <Field label="التواصل المفضل" type="select" options={CONTACT_PREFS} value={form.imam.preferredContact} onChange={v => setImam("preferredContact", v)} />
              </div>
              <Field label="ملاحظات على الإمام" type="textarea" rows={2} value={form.imam.notes} onChange={v => setImam("notes", v)} />
            </div>

            {/* Muezzin */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#7c3aed", borderBottom: `2px solid #7c3aed30`, paddingBottom: 6 }}>بيانات المؤذن</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="الاسم" placeholder="اسم المؤذن" value={form.muezzin.name} onChange={v => setMuezzin("name", v)} />
                <Field label="الجوال" placeholder="05xxxxxxxx" value={form.muezzin.phone} onChange={v => setMuezzin("phone", v)} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="البريد الإلكتروني" placeholder="email@example.com" value={form.muezzin.email} onChange={v => setMuezzin("email", v)} />
                <Field label="التواصل المفضل" type="select" options={CONTACT_PREFS} value={form.muezzin.preferredContact} onChange={v => setMuezzin("preferredContact", v)} />
              </div>
              <Field label="ملاحظات على المؤذن" type="textarea" rows={2} value={form.muezzin.notes} onChange={v => setMuezzin("notes", v)} />
            </div>

            {/* Stats */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#16a34a", borderBottom: `2px solid #16a34a30`, paddingBottom: 6 }}>الإحصائيات الدعوية</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                <Field label="الكلمات" type="number" placeholder="0" value={form.stats.talks} onChange={v => setStat("talks", v)} />
                <Field label="المحاضرات" type="number" placeholder="0" value={form.stats.lectures} onChange={v => setStat("lectures", v)} />
                <Field label="الدروس" type="number" placeholder="0" value={form.stats.lessons} onChange={v => setStat("lessons", v)} />
                <Field label="الأيام العلمية" type="number" placeholder="0" value={form.stats.scienceDays} onChange={v => setStat("scienceDays", v)} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 10 }}>
                <Field label="البرامج الدعوية" type="number" placeholder="0" value={form.stats.programs} onChange={v => setStat("programs", v)} />
                <Field label="المستفيدون" type="number" placeholder="0" value={form.stats.beneficiaries} onChange={v => setStat("beneficiaries", v)} />
                <Field label="آخر نشاط (تاريخ)" type="date" value={form.stats.lastActivity} onChange={v => setStat("lastActivity", v)} />
              </div>
            </div>

            <Field label="ملاحظات عامة" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Landmark} onClick={handleSave}>حفظ الموقع</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_MOSQUE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Programs Tab — البرامج الدعوية
   ───────────────────────────────────────────── */
const INITIAL_PROGRAM = {
  name: "", type: "", mosque: "", executor: "",
  date: "", attendance: "", report: "", attachments: "",
};

function ProgramsTab({ canEdit, programs, setPrograms, loading, mosques }) {
  const [filterType, setFilterType] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_PROGRAM);
  const [showToast, ToastView] = useToast();

  const mosqueOptions = mosques.map(m => m.name);
  const sheikhOptions = SHEIKHS.map(s => s.name);

  async function handleSave() {
    if (!form.name || !form.type || !form.executor) {
      showToast("الاسم والنوع والمنفذ مطلوبة", "error");
      return;
    }
    const payload = {
      name: form.name,
      type: form.type,
      mosque: form.mosque || "",
      executor: form.executor,
      program_date: form.date || "",
      attendance: Number(form.attendance) || 0,
      report: form.report || "",
      attachments: form.attachments || "",
    };
    try {
      const created = await createMosqueProgram(payload);
      setPrograms(prev => [mapProgramRow(created), ...prev]);
      setForm(INITIAL_PROGRAM);
      setShowAdd(false);
      showToast("تمت إضافة البرنامج بنجاح");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ البرنامج", "error");
    }
  }

  const filtered = programs.filter(p => filterType === "all" || p.type === filterType);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي البرامج" value={programs.length} icon={Award} color="#ea580c" />
        <KPICard title="أنواع البرامج" value={PROGRAM_TYPES.length} icon={Activity} color={C.teal} />
        <KPICard title="إجمالي الحضور" value={programs.reduce((s, p) => s + (Number(p.attendance) || 0), 0).toLocaleString()} icon={Users} color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {PROGRAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة برنامج</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name",       label: "اسم البرنامج", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "type",       label: "النوع", width: "140px" },
            { key: "mosque",     label: "الموقع", width: "150px", render: v => v || "—" },
            { key: "executor",   label: "المنفذ", width: "180px" },
            { key: "date",       label: "التاريخ", width: "120px", render: v => v || "—" },
            { key: "attendance", label: "الحضور", width: "100px", render: v => (Number(v) || 0).toLocaleString() },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`برنامج — ${selected.name}`} onClose={() => setSelected(null)} width={640}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>الموقع:</strong> {selected.mosque || "—"}</div>
            <div style={styles.detailRow}><strong>المنفذ:</strong> {selected.executor}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date || "—"}</div>
            <div style={styles.detailRow}><strong>عدد الحضور:</strong> {(Number(selected.attendance) || 0).toLocaleString()}</div>
            {selected.report && <div style={styles.detailRow}><strong>التقرير:</strong> {selected.report}</div>}
            {selected.attachments && <div style={styles.detailRow}><strong>المرفقات:</strong> {selected.attachments}</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة برنامج دعوي" onClose={() => { setShowAdd(false); setForm(INITIAL_PROGRAM); }} width={760}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم البرنامج" required placeholder="مثال: ملتقى شباب الحي" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="نوع البرنامج" type="select" required options={PROGRAM_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="الموقع" type="select" options={mosqueOptions} value={form.mosque} onChange={v => setForm({ ...form, mosque: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الشيخ المنفذ" type="select" required options={sheikhOptions} value={form.executor} onChange={v => setForm({ ...form, executor: v })} />
              <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Field label="عدد الحضور" type="number" placeholder="0" value={form.attendance} onChange={v => setForm({ ...form, attendance: v })} />
            <Field label="التقرير" type="textarea" rows={3} placeholder="ملخص البرنامج وأثره..." value={form.report} onChange={v => setForm({ ...form, report: v })} />
            <Field label="المرفقات (اسم ملف)" placeholder="report.pdf" value={form.attachments} onChange={v => setForm({ ...form, attachments: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Award} onClick={handleSave}>حفظ البرنامج</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PROGRAM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Talks Tab — الكلمات والمحاضرات
   ───────────────────────────────────────────── */
const INITIAL_TALK = {
  title: "", speaker: "", mosque: "", date: "",
  attendance: "", material: "", attachments: "", report: "",
};

function TalksTab({ canEdit, talks, setTalks, loading, mosques }) {
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_TALK);
  const [showToast, ToastView] = useToast();

  const mosqueOptions = mosques.map(m => m.name);
  const sheikhOptions = SHEIKHS.map(s => s.name);

  async function handleSave() {
    if (!form.title || !form.speaker) {
      showToast("العنوان والمتحدث مطلوبان", "error");
      return;
    }
    const payload = {
      title: form.title,
      speaker: form.speaker,
      mosque: form.mosque || "",
      talk_date: form.date || "",
      attendance: Number(form.attendance) || 0,
      material: form.material || "",
      attachments: form.attachments || "",
      report: form.report || "",
    };
    try {
      const created = await createMosqueTalk(payload);
      setTalks(prev => [mapTalkRow(created), ...prev]);
      setForm(INITIAL_TALK);
      setShowAdd(false);
      showToast("تمت إضافة الكلمة/المحاضرة بنجاح");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ الكلمة/المحاضرة", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الكلمات والمحاضرات" value={talks.length} icon={Megaphone} color="#7c3aed" />
        <KPICard title="إجمالي الحضور" value={talks.reduce((s, t) => s + (Number(t.attendance) || 0), 0).toLocaleString()} icon={Users} color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ ...styles.toolbar, justifyContent: "flex-end" }}>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة كلمة/محاضرة</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "title",      label: "العنوان", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "speaker",    label: "المتحدث", width: "180px" },
            { key: "mosque",     label: "الموقع", width: "150px", render: v => v || "—" },
            { key: "date",       label: "التاريخ", width: "120px", render: v => v || "—" },
            { key: "attendance", label: "الحضور", width: "100px", render: v => (Number(v) || 0).toLocaleString() },
            { key: "material",   label: "المادة", render: v => v || "—" },
          ]}
          data={talks}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`كلمة/محاضرة — ${selected.title}`} onClose={() => setSelected(null)} width={640}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={styles.detailRow}><strong>العنوان:</strong> {selected.title}</div>
            <div style={styles.detailRow}><strong>المتحدث:</strong> {selected.speaker}</div>
            <div style={styles.detailRow}><strong>الموقع:</strong> {selected.mosque || "—"}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date || "—"}</div>
            <div style={styles.detailRow}><strong>عدد الحضور:</strong> {(Number(selected.attendance) || 0).toLocaleString()}</div>
            {selected.material && <div style={styles.detailRow}><strong>المادة العلمية:</strong> {selected.material}</div>}
            {selected.report && <div style={styles.detailRow}><strong>التقرير:</strong> {selected.report}</div>}
            {selected.attachments && <div style={styles.detailRow}><strong>المرفقات:</strong> {selected.attachments}</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة كلمة/محاضرة" onClose={() => { setShowAdd(false); setForm(INITIAL_TALK); }} width={760}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="مثال: أهمية صلاة الجماعة" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="المتحدث" type="select" required options={sheikhOptions} value={form.speaker} onChange={v => setForm({ ...form, speaker: v })} />
              <Field label="الموقع" type="select" options={mosqueOptions} value={form.mosque} onChange={v => setForm({ ...form, mosque: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="عدد الحضور" type="number" placeholder="0" value={form.attendance} onChange={v => setForm({ ...form, attendance: v })} />
            </div>
            <Field label="المادة العلمية" type="textarea" rows={3} placeholder="ملخص المادة..." value={form.material} onChange={v => setForm({ ...form, material: v })} />
            <Field label="التقرير" type="textarea" rows={2} placeholder="تقرير عن الكلمة/المحاضرة..." value={form.report} onChange={v => setForm({ ...form, report: v })} />
            <Field label="المرفقات (اسم ملف)" placeholder="material.pdf" value={form.attachments} onChange={v => setForm({ ...form, attachments: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Megaphone} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_TALK); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Science Days Tab — الأيام العلمية
   ───────────────────────────────────────────── */
const INITIAL_SCIENCE_DAY = {
  name: "", date: "", lessonsCount: "",
  attendance: "", finalReport: "", photos: "",
};

function ScienceDaysTab({ canEdit, days, setDays, loading }) {
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_SCIENCE_DAY);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.name || !form.date) {
      showToast("الاسم والتاريخ مطلوبان", "error");
      return;
    }
    const payload = {
      name: form.name,
      day_date: form.date,
      lessons_count: Number(form.lessonsCount) || 0,
      attendance: Number(form.attendance) || 0,
      final_report: form.finalReport || "",
      photos: form.photos || "",
    };
    try {
      const created = await createMosqueScienceDay(payload);
      setDays(prev => [mapScienceDayRow(created), ...prev]);
      setForm(INITIAL_SCIENCE_DAY);
      setShowAdd(false);
      showToast("تمت إضافة اليوم العلمي بنجاح");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ اليوم العلمي", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الأيام العلمية" value={days.length} icon={GraduationCap} color={C.accent} />
        <KPICard title="إجمالي الدروس" value={days.reduce((s, d) => s + (Number(d.lessonsCount) || 0), 0)} icon={BookOpen} color={C.teal} />
        <KPICard title="إجمالي الحضور" value={days.reduce((s, d) => s + (Number(d.attendance) || 0), 0).toLocaleString()} icon={Users} color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ ...styles.toolbar, justifyContent: "flex-end" }}>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة يوم علمي</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name",         label: "اسم اليوم العلمي", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "date",         label: "التاريخ", width: "130px" },
            { key: "lessonsCount", label: "عدد الدروس", width: "120px", render: v => (Number(v) || 0).toLocaleString() },
            { key: "attendance",   label: "الحضور", width: "120px", render: v => (Number(v) || 0).toLocaleString() },
          ]}
          data={days}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`يوم علمي — ${selected.name}`} onClose={() => setSelected(null)} width={640}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>عدد الدروس:</strong> {(Number(selected.lessonsCount) || 0).toLocaleString()}</div>
            <div style={styles.detailRow}><strong>عدد الحضور:</strong> {(Number(selected.attendance) || 0).toLocaleString()}</div>
            {selected.finalReport && <div style={styles.detailRow}><strong>التقرير الختامي:</strong> {selected.finalReport}</div>}
            {selected.photos && <div style={styles.detailRow}><strong>الصور:</strong> {selected.photos}</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة يوم علمي" onClose={() => { setShowAdd(false); setForm(INITIAL_SCIENCE_DAY); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم اليوم العلمي" required placeholder="مثال: يوم علمي في فقه الصلاة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="عدد الدروس" type="number" placeholder="0" value={form.lessonsCount} onChange={v => setForm({ ...form, lessonsCount: v })} />
              <Field label="عدد الحضور" type="number" placeholder="0" value={form.attendance} onChange={v => setForm({ ...form, attendance: v })} />
            </div>
            <Field label="التقرير الختامي" type="textarea" rows={3} placeholder="تقرير ختامي عن اليوم..." value={form.finalReport} onChange={v => setForm({ ...form, finalReport: v })} />
            <Field label="الصور (اسم ملف)" placeholder="photos.zip" value={form.photos} onChange={v => setForm({ ...form, photos: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={GraduationCap} onClick={handleSave}>حفظ اليوم</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_SCIENCE_DAY); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   Programs / Talks / Science Days — Module-Level Constants
   ═════════════════════════════════════════════ */
export const PROGRAM_TYPES = [
  "كلمة دعوية",
  "محاضرة",
  "درس علمي",
  "دورة علمية",
  "يوم علمي",
  "برنامج موسمي",
  "برنامج شبابي",
  "برنامج أسري",
  "برنامج جاليات",
  "برنامج نسائي",
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16,
  },
  toolbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer",
  },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  /* View toggle */
  viewToggle: {
    display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`,
  },
  viewBtn: {
    padding: "6px 10px", border: "none", cursor: "pointer", fontSize: 14,
    fontFamily: "'Readex Pro'", transition: "all 0.2s",
  },

  /* Mosques Grid */
  mosquesGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16,
  },
  mosqueCard: { padding: 20, cursor: "pointer" },
  mosqueHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  mosqueIcon: {
    width: 48, height: 48, borderRadius: 12, display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  mosqueName: { margin: "12px 0 2px", fontSize: 16, fontWeight: 700, color: C.primary },
  mosqueDistrict: {
    margin: 0, fontSize: 13, color: C.textLight, display: "flex", alignItems: "center", gap: 4,
  },
  mosqueStats: {
    display: "flex", gap: 16, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}`,
  },
  mosqueStat: {
    display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.textLight,
  },
  mosqueFeatures: {
    display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10,
  },
  featureTag: {
    padding: "3px 8px", background: `${C.teal}10`, color: C.teal,
    borderRadius: 6, fontSize: 11, fontWeight: 500,
  },

  /* Section divider inside detail modal */
  sectionDivider: {
    marginTop: 8,
    paddingBottom: 4,
    borderBottom: `2px solid ${C.teal}30`,
    fontSize: 13,
    fontWeight: 700,
    color: C.teal,
  },

  /* Charts */
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20,
    marginBottom: 20,
  },
  chartCard: {
    padding: 20,
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  chartTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.primary,
  },
  legendGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "center",
    marginTop: 8,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
};
